import React, { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import { Button, Col, Row } from 'react-bootstrap';
import { Form } from 'formik';
import { useDispatch, useSelector } from 'react-redux';
import AccountManagerSelectField from './accountManager/AccountManagerSelectField';
import { FormDatePickerField, FormInputField } from '../../../common/components/formFields';
import DealerLocationForm from './DealerLocationForm';
import { scrollToFirstError } from '../../../common/helpers/scrollToFirstError';
import DealerUserForm from './DealerUserForm';
import DealerPaymentForm from './DealerPaymentForm';
import EditDealerNotesTable from '../EditDealerNotesTable';
import EditDealerChangesTable from '../EditDealerChangesTable';
import { DealerPendingActionTypes } from '../../../constants/DealerPendingActionTypes';
import { useQueryString } from '../../../common/helpers/queryString';
import { fetchDealerChanges, fetchDealerNotes } from '../../DealersActions';
import CommonErrorLabel from '../../../common/components/CommonErrorLabel';

const WEBSITE_VALIDATION_ERRORS_OBJECT_KEY_PREFIX = 'organizationForm.website';

function DealerForm({
  values,
  errors,
  showNotes,
  searchParamsNotes,
  changeOffsetNotes,
  dealerNotesRef,
  isDisabled,
}) {
  const query = useQueryString();
  const dispatch = useDispatch();
  const data = useSelector((state) => state.dealers.get('dealerDetails'));
  const dealerChanges = useSelector((state) => state.dealers.get('dealerChanges'));
  const itemsChanges = dealerChanges?.items;
  const totalItemsChanges = dealerChanges?.totalItems;
  const dealerNotes = useSelector((state) => state.dealers.get('dealerNotes'));
  const itemsNotes = dealerNotes?.items || null;
  const totalItemsNotes = dealerNotes?.totalItems;
  const validationErrors = useSelector((state) => state.dealers.get('validationErrors'));

  const [searchParamsChanges, setSearchParamsChanges] = useState({
    offset: query.get('page') ? +query.get('page') - 1 : 0,
  });

  useEffect(() => {
    if (data?.organization?.id) {
      dispatch(fetchDealerChanges(searchParamsChanges, data?.organization?.id));
    }
  }, [dispatch, searchParamsChanges, data]);

  useEffect(() => {
    if (data?.organization?.id) {
      dispatch(fetchDealerNotes(searchParamsNotes, data?.organization?.id));
    }
  }, [dispatch, searchParamsNotes, data]);

  const changeOffsetChanges = (value) => {
    setSearchParamsChanges((prevState) => ({ ...prevState, offset: value }));
  };

  useEffect(() => {
    if (errors && Object.keys(errors).length > 0) {
      scrollToFirstError();
    }
  }, [errors]);

  const preparePendingActionObj = (type) => {
    const pendingActions = data?.pendingActions;
    let action = null;

    if (type === DealerPendingActionTypes.ADD_ADDITIONAL_LOCATION && pendingActions) {
      const tabReturn = [];
      action = pendingActions.filter((item) => item.type === type);
      action.map((item) =>
        tabReturn.push({
          id: item?.id,
          version: item?.version,
          value: item?.value,
        }),
      );
      return tabReturn;
    }
    if (pendingActions) {
      action = pendingActions.find((item) => item.type === type);
      return {
        id: action?.id,
        version: action?.version,
        value: action?.value,
      };
    }
    return null;
  };

  return (
    <Form autoComplete="off">
      <Row>
        <Col md="3" className="mt-3">
          <AccountManagerSelectField
            name="accountManager"
            label="Account Manager"
            placeholder="Start typing..."
          />
        </Col>
      </Row>
      <h4 className="my-3 fw-bold">Company Details</h4>
      <Row>
        <Col md="9">
          <FormInputField
            name="officialCompanyName"
            label="Company Name"
            disabled={isDisabled}
            required
          />
        </Col>
        <Col md="3">
          <FormInputField
            name="vatNumber"
            label="VAT Registration Number"
            disabled={isDisabled}
            required
          />
        </Col>
      </Row>
      <Row>
        <Col md="12">
          <FormInputField name="tradeName" label="Trade Name" disabled={isDisabled} />
        </Col>
      </Row>
      <Row>
        <Col md="4">
          <FormInputField
            name="companyRegistrationNumber"
            label="Company Registration Number"
            disabled={isDisabled}
          />
        </Col>
        <Col md="4">
          <FormDatePickerField
            name="companyRegistrationDate"
            label="Registration Date"
            disabled
          />
        </Col>
        <Col md="4">
          <FormInputField name="website" label="Website Address" disabled={isDisabled} />
          <CommonErrorLabel
            value={validationErrors?.[WEBSITE_VALIDATION_ERRORS_OBJECT_KEY_PREFIX]}
          />
        </Col>
      </Row>
      <Row>
        <Col md="4">
          <FormInputField
            name="buyerNextGearId"
            label="Buyer NextGear ID"
            disabled={isDisabled}
          />
        </Col>
        <Col md="4">
          <FormInputField
            name="sellerNextGearId"
            label="Seller NextGear ID"
            disabled={isDisabled}
          />
        </Col>
      </Row>
      <DealerLocationForm
        locations={values.locations}
        disabled={isDisabled}
        preparePendingActionObj={preparePendingActionObj}
        organizationId={values?.organizationId}
      />
      <DealerUserForm
        errors={errors}
        disabled={isDisabled}
        preparePendingActionObj={preparePendingActionObj}
      />
      <DealerPaymentForm disabled={isDisabled} values={values} />
      <div ref={dealerNotesRef}>
        <EditDealerNotesTable
          data={{ items: itemsNotes, total: totalItemsNotes }}
          forceShow={showNotes}
          offset={searchParamsNotes.offset}
          changeOffset={changeOffsetNotes}
        />
      </div>
      <EditDealerChangesTable
        data={{ items: itemsChanges, total: totalItemsChanges }}
        offset={searchParamsChanges.offset}
        changeOffset={changeOffsetChanges}
      />
      <Button
        color="primary"
        className="mt-4 float-end rounded-pill btn-primary"
        type="submit"
        disabled={isDisabled}
      >
        Save
      </Button>
    </Form>
  );
}

DealerForm.propTypes = {
  values: PropTypes.object.isRequired,
  errors: PropTypes.object.isRequired,
  showNotes: PropTypes.bool,
  searchParamsNotes: PropTypes.object.isRequired,
  changeOffsetNotes: PropTypes.func.isRequired,
  dealerNotesRef: PropTypes.object.isRequired,
  isDisabled: PropTypes.bool,
};

DealerForm.defaultProps = {
  showNotes: false,
  isDisabled: false,
};

export default DealerForm;
